<script lang="ts">
	// Formating
	import { format } from 'svelte-i18n';
	//Design system
	import {
		Button,
		ActionsContainer,
		IconButton,
		Alert,
		Container,
	} from '@Shelly/shelly-design-system';
	//Helper
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { saveVCSettings } from 'src/helpers/virtual_helpers/virtual_components_settings_helpers';
	import { devices_settings, devices_status } from 'src/stores/device-runtime/status';
	import { getDeviceStatus, getDeviceSettings, readDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import { sortOn } from 'src/helpers/utilities';
	//Services
	import { gen2Request } from 'src/services/gen_2_http_reqs';
	//Componets
	import VirtualComponent from '../Components/VirtualComponent.svelte';
	import VCModalSettings from './VCModalSettings.svelte';
	import VCModalRemove from './VCModalRemove.svelte';
	import CreateVComponent from './CreateVComponent.svelte';
	import type { VirtualComponentInterface } from 'src/types/VirtualComponents';
	//Types
	import type { SettingRequest } from 'src/types/requests/settings/setting';
	import type { Device } from 'src/types/Device';
	import { virtualBthomeTranslationValues } from 'src/helpers/virtual_helpers/virtual_components_helpers';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import VirtualComponentV2 from '../ComponentsV2/VirtualComponentV2.svelte';

	$: statusStore = getDeviceStatus(deviceID);
	export let device: Device;
	export let showCreateButton = true;

	let showVCModalSettings = false;
	let showVCModalRemove = false;
	let showVCModalCreate = false;
	let waitForSaveResponse = false;
	let waitForCreateResponse = false;
	let selectedComponent: VirtualComponentInterface;
	let components: VirtualComponentInterface[] = [];
	let componentsList: string[] = [];

	const deviceID = real_id(device.id);

	const saveSettings = async (e: CustomEvent) => {
		waitForSaveResponse = true;
		const { settings, type, key } = e.detail;
		const res: SettingRequest = await saveVCSettings(device, type, settings);
		if (res?.isok) {
			showVCModalSettings = false;
			components.map((el) => {
				if (el.key == key) {
					el.config = settings;
				}
			});
			components = components;
		}
		waitForSaveResponse = false;
	};

	const removeVComponent = async (e: CustomEvent) => {
		waitForSaveResponse = true;
		const { key, config } = e.detail;
		let method = 'virtual.delete';
		let params: any = { key };
		if (/^(bthomesensor|bthomedevice):2\d{2}$/.test(key)) {
			method = /^bthomedevice/.test(key) ? 'bthome.deletedevice' : 'bthome.deletesensor';
			params = { id: config.id };
		}
		const res: SettingRequest = await gen2Request(device, method, params);
		if (res?.isok) {
			showVCModalRemove = false;
			components = components.filter((i) => i.key != key);
		}
		onSettingsResponse(res, device);
		waitForSaveResponse = false;
	};

	$: if ($statusStore) {
		const setting = devices_settings.getByRealId(deviceID);
		const status = devices_status.getByRealId(deviceID);
		const list: string[] = [];
		const trv = Object.keys(setting || {}).filter((i) => /^(blutrv):2\d{2}$/.test(i));
		for (const component in setting) {
			if (
				/^(number|boolean|text|enum|button|bthomesensor|bthomedevice):2\d{2}$/.test(component) &&
				status[component] &&
				!setting[component].owner &&
				!trv.find((i) => setting[setting[i]?.trv]?.addr == setting[component]?.addr)
			) {
				list.push(component);
			}
		}
		if (list.toString() != componentsList.toString()) {
			componentsList = list;
			components = [];
			for (let i = 0; i < componentsList.length; ++i) {
				const key = componentsList[i];
				const data = {
					key,
					config: setting[key],
					status: status[key],
				};
				if (!data.config?.meta) {
					data.config.meta = {
						cloud: [],
						ui: {
							icon: '',
							view: '',
						},
					};
				}
				components.push(data);
			}
			components = sortOn(components, 'key');
		}
	}
</script>

{#if components.length === 0}
	<Alert
		type="info"
		class="mb-s"
		heading={$format('no_virtual_components_title_new', {
			default: 'There are no {components} yet',
			values: {
				components: virtualBthomeTranslationValues(device.type),
			},
		})}
	/>
{:else}
	<Container class="mb-s" padding="0">
		{#each components as component}
			{#if $showNewLayout}
				<VirtualComponentV2 {device} {component} showHiddenComponent={true}>
					<ActionsContainer orientation="vertical" type="simple" class="ml-xs">
						<svelte:fragment slot="first">
							<IconButton
								aria-label={$format('button_show_modal_component_settings', {
									default: 'Show component settings',
								})}
								icon="fas fa-pen"
								variant="primary"
								on:click={() => {
									selectedComponent = component;
									showVCModalSettings = true;
								}}
							/>
						</svelte:fragment>
						<IconButton
							aria-label={$format('button_show_modal_remove_component', {
								default: 'Remove component',
							})}
							class="align-self-center"
							icon="fas fa-trash"
							variant="danger"
							slot="second"
							on:click={() => {
								selectedComponent = component;
								showVCModalRemove = true;
							}}
						/>
					</ActionsContainer>
				</VirtualComponentV2>
			{:else}
				<VirtualComponent {device} {component} showHiddenComponent={true}>
					<ActionsContainer orientation="vertical" type="simple" class="ml-xs">
						<svelte:fragment slot="first">
							<IconButton
								aria-label={$format('button_show_modal_component_settings', {
									default: 'Show component settings',
								})}
								icon="fas fa-pen"
								variant="primary"
								on:click={() => {
									selectedComponent = component;
									showVCModalSettings = true;
								}}
							/>
						</svelte:fragment>
						<IconButton
							aria-label={$format('button_show_modal_remove_component', {
								default: 'Remove component',
							})}
							class="align-self-center"
							icon="fas fa-trash"
							variant="danger"
							slot="second"
							on:click={() => {
								selectedComponent = component;
								showVCModalRemove = true;
							}}
						/>
					</ActionsContainer>
				</VirtualComponent>
			{/if}
		{/each}
	</Container>
{/if}
{#if showCreateButton}
	<div class="mb-s ml-xs mr-xs">
		<Button
			size="m"
			width="full"
			loading={waitForCreateResponse}
			on:click={() => (showVCModalCreate = true)}
			>{$format('virtual_components_create_component', {
				default: 'Create virtual component',
			})}</Button
		>
	</div>
{/if}
{#if showVCModalSettings}
	<VCModalSettings
		component={selectedComponent}
		waitForResponse={waitForSaveResponse}
		bind:showModal={showVCModalSettings}
		on:edit={saveSettings}
	/>
{:else if showVCModalRemove}
	<VCModalRemove
		component={selectedComponent}
		waitForResponse={waitForSaveResponse}
		bind:showModal={showVCModalRemove}
		on:remove={removeVComponent}
	/>
{:else if showVCModalCreate}
	<CreateVComponent {device} bind:showWizard={showVCModalCreate} />
{/if}
